import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDropdown, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Editor, Toolbar } from 'ngx-editor';
import { Subscription } from 'rxjs';
import { RegExp } from 'src/app/core/config/regex';
import { Employee, Milestone, Project, Task, TaskAttachment, UiSubtask } from 'src/app/core/models/project-pilot.model';
import { ConfirmationPopupComponent } from 'src/app/shared/component/confirmation-popup/confirmation-popup.component';
import { Context } from 'src/app/shared/models/context';
import { Message, TableData } from 'src/app/shared/models/domain';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DomainService } from 'src/app/shared/services/domain.service';
import { ProjectPilotService } from 'src/app/shared/services/project-pilot.service';

@Component({
  selector: 'app-tasks',
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss']
})
export class TasksComponent implements OnInit, OnDestroy {
  @ViewChildren('taskTitle') taskTitles!: QueryList<ElementRef<HTMLTextAreaElement>>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('filterDropdown') filterDropdown: NgbDropdown;
  public task!: FormGroup;
  public milestones: Milestone[] = [];
  public searchKeyword: string = "";
  public statuses: string[] = [];
  public priorities: string[] = [];
  public searchIndex: number = 0;
  private subscription!: Subscription;
  public employees: Employee[] = [];
  public isFilterDropdownOpen: boolean = false;
  @ViewChild('tableBody', { static: false }) tableBody!: ElementRef;
  @ViewChild('dropdown') dropdown: NgbDropdown;
  public selectedTasks: number[] = [];
  public showSidePopup: boolean = false;
  public newTask!: Task | UiSubtask;
  public isSubItem: boolean = false;
  public parentTaskId: number | null = null;
  public showDatePicker: boolean = false;
  public isDeleteInProgress = false;
  public activeTab: string = 'tasks';
  public dropdownDirections: { [taskId: string]: 'top' | 'bottom' } = {};
  public filteredTasks: Task[] = [];
  public sortDirection: 'asc' | 'desc' | null = null;
  public subtasks: UiSubtask[] = [];
  public projects: Project[] = [];
  public filteredProjects: Project[] = [];
  public filteredMilestones: Milestone[] = [];
  public taskss: Task[] = [];
  public selectedFilter: string = '';
  public deadlineDate: Date;
  public isLoading: boolean = false;
  public tableData: TableData = { content: [] } as TableData;
  public pageSizes: number[] = [];
  private pageDataKey: string = 't';
  private scrollTimeout: any;
  public projectId: number | null = null;
  public selectedStatus: string;
  public selectedPriority: string;
  public selectedAssigneeId: number | null = null;
  public context: Context;
  public privilege: string = 'VIEW';
  public hoveredTaskIndex: number | null = null;
  public focusedTaskIndex: number | null = null;

  constructor(
    private fb: FormBuilder,
    private projectPilotService: ProjectPilotService,
    private datePipe: DatePipe,
    private authService: AuthService,
    private commonService: CommonService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
    private domainService: DomainService
  ) { }

  ngOnInit(): void {
    this.pageSizes = this.commonService.getDefaultPageSizes();
    this.tableData = this.commonService.getDefaultPageData(this.pageDataKey);
    this.tableData.number = 0;
    this.subscription = this.authService.getContext().subscribe(context => {
      this.context = context;
      if (context.projectId || this.context?.projectId) {
        this.getEmployeeProjectPrivilege();
        this.getTasksByPage(true, 0);
        this.getFiltersFromLocalStorage();
      }
    });
    this.initTaskForm();
    this.getMilestones();
    this.getEmployees();
    this.getTaskPriorities();
    this.getTaskStatuses();
    this.getStatuses();
    this.fetchProjects();
    this.priorities = this.commonService.getPriorities();
    this.projectPilotService.fetchProjects(false).subscribe((projects: Project[]) => {
      this.projects = projects;
      if (this.projects.length > 0) {
        const selectedProject = this.projects[0];
        this.projectId = selectedProject.id;
      }
    });
    this.isFilterDropdownOpen = false;
  }

  private initTaskForm() {
    this.task = this.fb.group({ tasks: this.fb.array([]) });
  }

  public onTableScroll(event: Event): void {
    console.log("scroll")
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = setTimeout(() => {
      const element = event.target as HTMLElement;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;
      const isAtBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - 5;
      if (isAtBottom && !this.isLoading && this.hasMoreTasks()) {
        this.getTasksByPage(false, this.tableData.number);
      }
    }, 150);
  }

  public hasMoreTasks(): boolean {
    const totalElements = this.tableData.totalElements || 0;
    const loadedTasks = (this.task.get('tasks') as FormArray).length;
    return loadedTasks < totalElements;
  }

  private getTaskPriorities() {
    this.projectPilotService.getTaskPriorities().subscribe({
      next: (priorities: string[]) => {
        this.priorities = priorities;
      },
    });
  }

  private getTaskStatuses() {
    this.projectPilotService.getTaskStatuses().subscribe({
      next: (statuses: string[]) => {
        this.statuses = statuses;
      },
    });
  }

  public getEmployeeProjectPrivilege(): void {
    this.projectPilotService.getEmployeeProjectPrivilege(this.context.projectId).subscribe({
      next: (response: Message) => {
        if (!response.failed) {
          this.privilege = response.payload;
          this.commonService.setToastr(200, response.message);
          this.cdr.detectChanges();
        } else {
          this.privilege = 'VIEW';
          this.commonService.setToastr(400, response.message);
        }
      },
    });
  }

  private setTask(task?: Task) {
    if (!task) task = {
      id: null,
      title: '',
      sequence: this.tasks.value.length + 1,
      desc: '',
      status: 'NOT_STARTED',
      priority: 'LOW',
      deadLine: '',
      estimatedHour: null,
      completedHour: null,
      startDate: '',
      endDate: '',
      code: '',
      performance: null,
      disabled: false,
      projectId: this.context?.projectId,
      projectName: '',
      milestoneId: null,
      milestoneName: '',
      ownerId: this.context?.employeeId,
      ownerName: '',
      assignedToId: null,
      assignedToName: '',
      selected: false,
      editTask: false,
      isExpanded: false,
      assignedToAvatar: '',
      isEditing: false,
      showStatusDropdown: false,
      showAssigneeDropdown: false,
      showPriorityDropdown: false,
      showProjectDropdown: false,
      showMilestoneDropdown: false,
      showStartDate: false,
      showCompletedDate: false
    } as Task;
    return this.fb.group({
      id: this.fb.control(task.id, []),
      title: this.fb.control(task.title, [Validators.required, Validators.pattern(RegExp.label)]),
      sequence: this.fb.control(task.sequence, []),
      desc: this.fb.control(task.desc, []),
      status: this.fb.control(task.status, []),
      priority: this.fb.control(task.priority, []),
      deadLine: this.fb.control(task?.deadLine ? this.datePipe.transform(task.deadLine, 'yyyy-MM-dd') : '', []),
      estimatedHour: this.fb.control(task.estimatedHour, []),
      completedHour: this.fb.control(task.completedHour, []),
      startDate: this.fb.control(task.startDate, []),
      code: this.fb.control(task.code, []),
      endDate: this.fb.control(task.endDate, []),
      performance: this.fb.control(task.performance, []),
      disabled: this.fb.control(task.disabled, []),
      projectId: this.fb.control(task.projectId, []),
      projectName: this.fb.control(task.projectName, []),
      milestoneId: this.fb.control(task.milestoneId, []),
      milestoneName: this.fb.control(task.milestoneName, []),
      ownerId: this.fb.control(task.ownerId, [Validators.required]),
      ownerName: this.fb.control(task.ownerName, []),
      assignedToId: this.fb.control(task.assignedToId, []),
      assignedToName: this.fb.control(task.assignedToName, []),
      assignedToAvatar: this.fb.control(task.assignedToAvatar, []),
      selected: this.fb.control(false, []),
      editTask: this.fb.control(false, []),
      isExpanded: this.fb.control(task.isExpanded, []),
      isEditing: this.fb.control(task.isEditing, []),
      showStatusDropdown: this.fb.control(task.showStatusDropdown, []),
      showAssigneeDropdown: this.fb.control(task.showAssigneeDropdown, []),
      showPriorityDropdown: this.fb.control(task.showPriorityDropdown, []),
      showProjectDropdown: this.fb.control(task.showProjectDropdown, []),
      showMilestoneDropdown: this.fb.control(task.showMilestoneDropdown, []),
      taskAttachments: this.fb.array(task?.taskAttachments?.map(attachment => this.setTaskAttachment(attachment)) || []),
      showStartDate: this.fb.control(task.showStartDate, []),
      showCompletedDate: this.fb.control(task.showCompletedDate, [])
    });
  }

  private setTaskAttachment(attachment?: TaskAttachment) {
    if (!attachment) attachment = {
      id: null,
      fileUrl: '',
      disabled: false,
      taskId: null,
      taskName: ''
    } as TaskAttachment;
    return this.fb.group({
      id: this.fb.control(attachment.id, []),
      fileUrl: this.fb.control(attachment.fileUrl, []),
      disabled: this.fb.control(attachment.disabled, []),
      taskId: this.fb.control(attachment.taskId, []),
      taskName: this.fb.control(attachment.taskName, [])
    });
  }

  public openFilePicker(task: Task, element: HTMLInputElement): void {
    if (!task?.id) return;
    element.click();
  }

  public closeAllDropdowns(task?: any) {
    task.showStatusDropdown = false;
    task.showAssigneeDropdown = false;
    task.showPriorityDropdown = false;
    task.showMilestoneDropdown = false;
  }

  public onFileSelected(event: Event, task: Task, index: number): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const maxSizeInMB = 20;
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
      if (file.size > maxSizeInBytes) {
        this.commonService.setToastr(500, `File size exceeds ${maxSizeInMB}MB limit.`);
        return;
      }
      this.upsertAttachment(file, task, index);
    }
  }

  get tasks() {
    return this.task.get('tasks') as FormArray;
  }

  public taskAttachments(i: number) {
    return this.tasks.at(i).get('taskAttachments') as FormArray;
  }

  public upsertTask(taskForm: FormGroup): void {
    if (this.privilege !== 'VIEW') {
      if (taskForm.valid) {
        const taskData = taskForm.value;
        if (taskData.estimatedHour !== null && taskData.estimatedHour < 0) {
          this.commonService.setToastr(400, 'Estimated hours cannot be negative.');
        }
        if (taskData.id) {
          this.projectPilotService.updateTask(taskData.id, taskData).subscribe({
            next: (response: Message) => {
              if (!response.failed) {
                this.getAndPatchTask(parseInt(response.payload), taskForm);
              }
            },
            error: (err) => console.error('Error updating task:', err)
          });
        } else {
          this.projectPilotService.createTask(taskData).subscribe({
            next: (response: Message) => {
              if (!response.failed) {
                this.getAndPatchTask(parseInt(response.payload), taskForm);
              }
            },
            error: (err) => console.error('Error creating task:', err)
          });
        }
      }
    }
  }

  public upsertAttachment(file: File, task: Task, index: number): void {
    if (this.privilege !== 'VIEW') {
      if (file?.size > 0 && task?.id) {
        const attachment: TaskAttachment = { id: null, fileUrl: '', disabled: false, taskId: task.id, taskName: task.title };
        this.projectPilotService.createTaskAttachment(attachment).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              const attachmentId = parseInt(response.payload);
              this.uploadTaskAttachment(attachmentId, file, index);
            }
          },
          error: (err) => console.error('Error creating task attachment:', err)
        });
      }
    }
  }

  public convertMinutesToHours(decimalHour: number) {
    return this.commonService.convertMinutesToHours(decimalHour);
  }

  getCompletedHourTooltip(minutes: number): string {
    const hours = minutes / 60;
    return `${hours.toFixed(2)} hours`;
  }

  private getAndPatchAttachment(attachmentId: number, index: number): void {
    this.projectPilotService.getTaskAttachment(attachmentId).subscribe({
      next: (attachment: TaskAttachment) => {
        if (!attachment || !attachment.id) return;
        this.taskAttachments(index).push(this.setTaskAttachment(attachment));
      },
    });
  }

  private uploadTaskAttachment(attachmentId: number, file: File, index: number): void {
    if (this.privilege !== 'VIEW') {
      if (!attachmentId) return;
      const formData = new FormData();
      formData.append('file', file);
      this.projectPilotService.uploadTaskAttachment(attachmentId, formData).subscribe({
        next: (response: Message) => {
          if (!response.failed) {
            this.getAndPatchAttachment(attachmentId, index);
          }
        },
        error: (err) => console.error('Error uploading file:', err)
      });
    }
  }

  public getFilePreviewIcon(url: string): string {
    return this.commonService.getFilePreviewIcon(url);
  }

  private getMilestones(): void {
    this.projectPilotService.getMilestones().subscribe({
      next: (milestones: Milestone[]) => {
        this.milestones = milestones;
      },
    });
  }

  private getEmployees(): void {
    this.projectPilotService.getEmployees().subscribe({
      next: (employees: Employee[]) => {
        this.employees = employees;
      },
    });
  }

  public toggleDatePicker(): void {
    this.showDatePicker = !this.showDatePicker;
  }

  public toggleAssigneeDropdown(task: Task): void {
    this.resetDropdownToggleStatus();
    task.showAssigneeDropdown = !task.showAssigneeDropdown;
    this.toggledropdownVisibility(task);
  }

  public toggleMilestoneDropdown(task: Task): void {
    this.resetDropdownToggleStatus();
    task.showMilestoneDropdown = !task.showMilestoneDropdown;
    this.toggledropdownVisibility(task);
  }

  public toggleDueDate(task: Task, dueDateRef: any): void {
    this.resetDropdownToggleStatus();
    task.showDueDate = !task.showDueDate;
    this.toggledropdownVisibility(task);
    setTimeout(() => {
      dueDateRef.showPicker?.();
      dueDateRef.focus()
    }, 0);
  }

  public togglePriorityDropdown(task: Task): void {
    this.resetDropdownToggleStatus();
    task.showPriorityDropdown = !task.showPriorityDropdown;
    this.toggledropdownVisibility(task);
  }

  public toggledropdownVisibility(task: any): void {
    const trigger = document.getElementById('trigger-' + task.id);
    const dropdownHeight = 200;
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      this.dropdownDirections[task.id] = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight ? 'top' : 'bottom';
    }
  }

  public toggleStatusDropdown(task: any) {
    this.resetDropdownToggleStatus();
    task.showStatusDropdown = !task.showStatusDropdown;
    this.toggledropdownVisibility(task);
  }

  private resetDropdownToggleStatus() {
    this.tasks.value.forEach(task => {
      task.showStatusDropdown = false;
      task.showAssigneeDropdown = false;
      task.showPriorityDropdown = false;
      task.showMilestoneDropdown = false;
      task.showDueDate = false;

    });
  }

  public setAssignee(taskForm: FormGroup, employee: Employee): void {
    taskForm.get('assignedToId')?.setValue(employee?.employeeId);
    taskForm.get('assignedToName')?.setValue(employee?.employeeName);
    taskForm.get('assignedToAvatar')?.setValue(employee?.avatar || null);
    taskForm.get('showAssigneeDropdown')?.setValue(false);
    this.upsertTask(taskForm);
    this.searchKeyword = "";
  }

  public setMilestone(taskForm: FormGroup, milestone: Milestone): void {
    taskForm.get('milestoneId')?.setValue(milestone?.id);
    taskForm.get('milestoneName')?.setValue(milestone?.title);
    taskForm.get('showMilestoneDropdown')?.setValue(false);
    this.upsertTask(taskForm);
    this.searchKeyword = "";
  }

  public setPriority(taskForm: FormGroup, priority: string): void {
    taskForm.get('priority')?.setValue(priority);
    this.upsertTask(taskForm);
  }

  public setStatus(taskForm: FormGroup, status: string): void {
    if (this.privilege === 'VIEW') {
      this.commonService.setToastr(400, 'Access denied: insufficient privilege');
      return;
    }

    if (!taskForm.value.id) {
      this.commonService.setToastr(400, 'Task ID missing: Cannot update status.');
      return;
    }
    if (status === 'HOLD' && this.privilege !== 'DELETE') {
      this.commonService.setToastr(400, 'Access denied: Only OWNER can set status to HOLD.');
      return;
    }
    if (status === 'IN_PROGRESS') {
    this.tasks.controls.forEach((ctrl: FormGroup) => {
      if (
        ctrl !== taskForm &&
        ctrl.get('status')?.value === 'IN_PROGRESS'
      ) {
        ctrl.get('status')?.setValue('HOLD');
        // Optionally, persist this change to backend:
        if (ctrl.get('id')?.value) {
          this.projectPilotService.upsertTaskStatus(ctrl.get('id')?.value, 'HOLD').subscribe();
        }
      }
    });
  }
    taskForm.get('status')?.setValue(status);
    this.projectPilotService.upsertTaskStatus(taskForm.value.id, status).subscribe({
      next: (response: Message) => {
        if (!response.failed) {
          this.getAndPatchTask(taskForm.value.id, taskForm);
        }
      },
      error: (err) => console.error('Error updating task status:', err)
    });
    taskForm.get('showStatusDropdown')?.setValue(false);
  }

  public resizeTextArea(textArea: HTMLTextAreaElement) {
    textArea.style.height = 'auto';
    textArea.style.height = textArea.scrollHeight + 'px';
  }

  private getAndPatchTask(taskId: number, taskForm: FormGroup,): void {
    const t: Task = taskForm.value;
    this.projectPilotService.getTask(taskId).subscribe({
      next: (task: Task) => {
        taskForm.patchValue({
          ...task,
          deadLine: task?.deadLine ? this.datePipe.transform(task.deadLine, 'yyyy-MM-dd') : '',
          editTask: false
        } as Task);
      },
    });
    // this.getTasksByPage(true, 0);
  }

  public handleKeyDown(event: KeyboardEvent, taskForm: FormGroup, type: string): void {
    const list: any =
      type === 'milestone' ? this.milestones.filter(m => m.title.toLowerCase().includes(this.searchKeyword?.toLowerCase() || '')) :
        type === 'assignee' ? this.employees.filter(e => e.employeeName.toLowerCase().includes(this.searchKeyword?.toLowerCase() || '')) :
          type === 'priority' ? this.priorities
            : [];
    if (event.key === 'ArrowDown') {
      this.searchIndex = (this.searchIndex + 1) % list.length;
      event.preventDefault();
    } else if (event.key === 'ArrowUp') {
      this.searchIndex = (this.searchIndex - 1 + list.length) % list.length;
      event.preventDefault();
    } else if (event.key === 'Enter') {
      const selected = list[this.searchIndex];
      if (selected) {
        if (type === 'assignee') {
          this.setAssignee(taskForm, selected);
        } else if (type === 'milestone') {
          this.setMilestone(taskForm, selected);
        } else if (type === 'priority') {
        }

      }
    } else if (event.key === 'Tab') {
      taskForm.get('showAssigneeDropdown')?.setValue(false);
      taskForm.get('showMilestoneDropdown')?.setValue(false);
      this.upsertTask(taskForm);
    }
  }

  public onFieldKeyDown(event: KeyboardEvent, taskForm: FormGroup): void {
    if (!taskForm) {
      return;
    }
    if (event.key === 'Tab' || event.key === 'Enter') {
      this.upsertTask(taskForm);
    }
  }

  public getTaskStatus(status: string): string {
    switch (status) {
      case 'NOT_STARTED':
        return 'Not Started';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'COMPLETED':
        return 'Completed';
      case 'HOLD':
        return 'On Hold';
      case 'REASSIGNED':
        return 'Reassigned';
      default:
        return status;
    }
  }

  public applyFilters(dropdown?: NgbDropdown): void {
    if (!this.projectId) {
      this.commonService.setToastr(400, 'Please select a project!');
      return;
    }
    this.saveFilters();
    this.tableData.number = 0;
    this.getTasksByPage(true, 0);
    dropdown?.close();
    this.isFilterDropdownOpen = false;
    this.showDatePicker = false;
  }

  public saveFilters(): void {
    const filterData: any = {};
    if (this.selectedStatus) {
      filterData.selectedStatus = this.selectedStatus;
    }
    if (this.selectedAssigneeId) {
      filterData.selectedAssigneeId = this.selectedAssigneeId;
    }
    if (this.selectedPriority) {
      filterData.selectedPriority = this.selectedPriority;
    }
    if (this.deadlineDate) {
      filterData.deadlineDate = this.deadlineDate;
    }
    if (Object.keys(filterData).length > 0) {
      this.commonService.setLocalItem('task-filters', filterData);
    } else {
      this.commonService.removeLocalItem('task-filters');
    }
  }

  public getFiltersFromLocalStorage(): void {
    const savedFilters = this.commonService.getLocalItem('task-filters');
    if (savedFilters) {
      this.selectedStatus = savedFilters.selectedStatus;
      this.selectedAssigneeId = savedFilters.selectedAssigneeId;
      this.selectedPriority = savedFilters.selectedPriority;
      this.deadlineDate = savedFilters.deadlineDate;
    }
  }

  public toggleDropdown(dropdown: NgbDropdown): void {
    if (this.isFilterDropdownOpen) {
      dropdown.close();
      this.isFilterDropdownOpen = false;
    } else {
      dropdown.open();
      this.isFilterDropdownOpen = true;
    }
  }

  public getTasksByPage(clear: boolean = true, pageNumber: number = this.tableData.number): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    const pageSize = Number(this.tableData.size || 10);
    const formattedDeadline: string | undefined = this.deadlineDate
      ? this.datePipe.transform(this.deadlineDate, 'dd-MM-yyyy')
      : undefined;
    this.projectPilotService.getTasksByPage(pageNumber, pageSize, this.context.projectId, this.selectedStatus || undefined, this.selectedAssigneeId || undefined, this.selectedPriority || undefined, formattedDeadline).subscribe({
      next: (tableData: TableData) => {
        this.tableData = {
          ...tableData,
          number: pageNumber + 1,
          size: pageSize
        };
        if (clear) {
          (this.task.get('tasks') as FormArray).clear();
        }
        const sortedTasks = [...tableData.content].sort((a, b) => {
          return (a.code || '').localeCompare(b.code || '', undefined, { numeric: true, sensitivity: 'base' });
        });
        sortedTasks.forEach((task: Task) => {
          (this.task.get('tasks') as FormArray).push(this.setTask(task));
        });
        // tableData.content.forEach((task: Task) => {
        //   (this.task.get('tasks') as FormArray).push(this.setTask(task));
        // });
        setTimeout(() => {
          this.taskTitles?.forEach((title) => {
            this.resizeTextArea(title.nativeElement);
          });
        });
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    });
  }

  public hasActiveFilters(): boolean {
    return !!(this.selectedStatus || this.selectedAssigneeId || this.selectedPriority || this.deadlineDate);
  }

  public getStatusDisplayText(): string {
    const statusMap = {
      'NOT_STARTED': 'To-do',
      'IN_PROGRESS': 'In progress',
      'COMPLETED': 'Completed',
      'HOLD': 'On hold',
      'REASSIGNED': 'Re-assigned'
    };
    return statusMap[this.selectedStatus] || this.selectedStatus;
  }

  public getAssigneeDisplayText(): string {
    const assignee = this.employees.find(emp => emp.employeeId === this.selectedAssigneeId);
    return assignee ? assignee.employeeName : 'Unknown';
  }

  public clearFilter(filterType: string): void {
    switch (filterType) {
      case 'status':
        this.selectedStatus = null;
        break;
      case 'assignee':
        this.selectedAssigneeId = null;
        break;
      case 'priority':
        this.selectedPriority = null;
        break;
      case 'deadline':
        this.deadlineDate = null;
        break;
      default:
        return;
    }
    this.applyFilters();
  }

  public onTaskUpdated(updatedTask: Task): void {
    const index = this.tasks.controls.findIndex(t => t.get('id')?.value === updatedTask.id);
    if (index !== -1) {
      const taskForm = this.tasks.at(index) as FormGroup;
      taskForm.patchValue(updatedTask);
      if (updatedTask.taskAttachments) {
        const attachmentsArray = taskForm.get('taskAttachments') as FormArray;
        attachmentsArray.clear();
        updatedTask.taskAttachments.forEach(att => {
          attachmentsArray.push(this.setTaskAttachment(att));
        });
      }
      setTimeout(() => {
        this.taskTitles?.forEach((title) => {
          this.resizeTextArea(title.nativeElement);
        });
      });
    } else {
      this.tasks.push(this.setTask(updatedTask));
      setTimeout(() => {
        this.taskTitles?.forEach((title) => {
          this.resizeTextArea(title.nativeElement);
        });
      });
    }
  }

  private getStatuses(): void {
    this.statuses = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'ON_HOLD'].map(status =>
      status
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    );
  }

  //  public addNewTask(): void {
  //   this.tasks.push(this.setTask());
  //   setTimeout(() => {
  //     this.tableBody.nativeElement.scrollTop = this.tableBody.nativeElement.scrollHeight;
  //     const newTaskIndex = this.tasks.length - 1;
  //     const titleInput = this.tableBody.nativeElement.querySelector(
  //       `.table-row:nth-child(${newTaskIndex + 1}) .name-input`
  //     );
  //     if (titleInput) {
  //       (titleInput as HTMLTextAreaElement).focus();
  //     }
  //   }, 0);
  // }

  public addNewTask(): void {
    this.tasks.push(this.setTask());
    setTimeout(() => {
      const tableBodyEl = this.tableBody?.nativeElement;
      if (tableBodyEl && tableBodyEl.scrollHeight > tableBodyEl.clientHeight) {
        // Only scroll if content overflows
        tableBodyEl.scrollTop = tableBodyEl.scrollHeight;
      }
      const newTaskIndex = this.tasks.length - 1;
      const titleInput = tableBodyEl?.querySelector(
        `.table-row:nth-child(${newTaskIndex + 1}) .name-input`
      );
      if (titleInput) {
        (titleInput as HTMLTextAreaElement).focus();
      }
    }, 0);
  }

  public openSidePopup(isSubItem = false, parentId: number | null, task: any): void {
    this.isSubItem = isSubItem;
    this.parentTaskId = parentId;
    this.newTask = task
      ? { ...this.createEmptyTask(), ...task, isEditing: true } as Task
      : this.createEmptyTask();
    if (isSubItem && parentId) {
      (this.newTask as UiSubtask).taskId = parentId;
    }
    this.showSidePopup = true;
  }

  public closeSidePopup(): void {
    this.showSidePopup = false;
    this.newTask = this.createEmptyTask();
    this.isSubItem = false;
    this.parentTaskId = null;
  }

  private fetchProjects(): void {
    this.projectPilotService.fetchProjects(false).subscribe({
      next: (projects: Project[]) => {
        this.projects = projects;
        if (this.projects.length > 0 && !this.projectId) {
          this.projectId = this.projects[0].id;
        }
      },
      error: (err) => console.error('Error fetching projects:', err)
    });
  }

  private createEmptyTask(): Task | UiSubtask {
    return {
      id: null,
      title: '',
      desc: '',
      sequence: 0,
      status: 'NOT_STARTED',
      priority: 'LOW',
      deadLine: '',
      estimatedTime: '',
      completedTime: '',
      performance: 'BELOW_EXPECTATION',
      disabled: false,
      projectId: null,
      projectName: '',
      milestoneId: null,
      milestoneName: '',
      ownerId: this.context?.employeeId,
      ownerName: '',
      assignedToId: null,
      assignedToName: '',
      isExpanded: false,
      isEditing: true,
      showStatusDropdown: false,
      showAssigneeDropdown: false,
      showPriorityDropdown: false
    };
  }

  public toggleTaskSelection(taskId: number): void {
    if (!taskId) return;
    const taskIndex = this.tasks.controls.findIndex(t => t.get('id')?.value === taskId);
    if (taskIndex === -1) return;
    const isSelected = this.selectedTasks.includes(taskId);
    if (isSelected) {
      this.selectedTasks = this.selectedTasks.filter(id => id !== taskId);
      this.tasks.at(taskIndex).get('selected')?.setValue(false);
    } else {
      this.selectedTasks.push(taskId);
      this.tasks.at(taskIndex).get('selected')?.setValue(true);
    }
    this.cdr.detectChanges();
  }

  public deleteSelectedTasks(): void {
    if (this.privilege === 'VIEW') {
      this.commonService.setToastr(400, 'Access denied: insufficient privilege to delete');
      return;
    }
    const modalRef = this.modalService.open(ConfirmationPopupComponent);
    modalRef.componentInstance.title = "Task Deletion";
    modalRef.componentInstance.message = `Delete ${this.selectedTasks.length} selected task(s)?`;
    modalRef.result.then((isConfirmed: boolean) => {
      if (isConfirmed) {
        this.isDeleteInProgress = true;
        const tasksArray = this.task.get('tasks') as FormArray;
        const idsToDelete: number[] = [...this.selectedTasks];
        if (idsToDelete.length > 0) {
          this.projectPilotService.deleteTasks(idsToDelete).subscribe({
            next: (response: Message) => {
              this.isDeleteInProgress = false;
              if (!response.failed) {
                idsToDelete.forEach(taskId => {
                  const index = tasksArray.controls.findIndex(t => t.get('id')?.value === taskId);
                  if (index !== -1) {
                    tasksArray.removeAt(index);
                  }
                });
                this.selectedTasks = [];
                tasksArray.controls.forEach((task: FormGroup) => {
                  task.get('selected')?.setValue(false);
                });
                this.commonService.setToastr(200, response.message);
                this.getTasksByPage(true, 0);
                this.cdr.detectChanges();
              } else {
                this.commonService.setToastr(400, response.message);
              }
            },
            error: (error: HttpErrorResponse) => {
              this.isDeleteInProgress = false;
              this.commonService.setToastr(500, error.error?.message || error.message || 'Deletion failed');
              this.getTasksByPage(true, 0);
              this.cdr.detectChanges();
            }
          });
        } else {
          tasksArray.controls.forEach((task: FormGroup, index: number) => {
            if (!task.get('id')?.value && task.get('selected')?.value) {
              tasksArray.removeAt(index);
            }
          });
          this.selectedTasks = [];
          tasksArray.controls.forEach((task: FormGroup) => {
            task.get('selected')?.setValue(false);
          });
          this.isDeleteInProgress = false;
          this.cdr.detectChanges();
        }
      }
    });
  }

  get isAllTasksSelected(): boolean {
    if (this.tasks.length === 0) return false;
    return this.tasks.controls.every((task: FormGroup) => task.get('selected')?.value);
  }

  public toggleAllTasks(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.selectedTasks = [];
    this.tasks.controls.forEach((task: FormGroup) => {
      const taskId = task.get('id')?.value;
      if (taskId) {
        task.get('selected')?.setValue(isChecked);
        if (isChecked) {
          this.selectedTasks.push(taskId);
        }
      }
    });
    this.cdr.detectChanges();
  }

  public setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.applyFilters();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
